import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, SearchIcon, Loader2Icon, DatabaseIcon, TargetIcon, PenLineIcon, CheckCircle2Icon, FileSpreadsheetIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, PencilIcon, FileIcon, FolderIcon, ArrowRightIcon, ChevronDownIcon, ClockIcon, SettingsIcon, SendIcon } from 'lucide-react';
const STEP_DURATION = 15000;
const steps = [{
  id: 1,
  title: 'Describe your ideal customer',
  description: 'Describe exactly who you want to target in plain English. No complex filters needed.',
  icon: SearchIcon
}, {
  id: 2,
  title: 'The agent searches & scores',
  description: 'The agent queries our 200M+ local business database and ranks leads based on your criteria.',
  icon: TargetIcon
}, {
  id: 3,
  title: 'Review and execute',
  description: 'Approve the generated lead list and let the agent draft personalized outreach automatically.',
  icon: SparklesIcon
}];
function Step1Illustration() {
  const [activeCard, setActiveCard] = useState(0);
  const [cardProgress, setCardProgress] = useState(0);
  useEffect(() => {
    setCardProgress(0);
    const startTime = Date.now();
    const duration = 3500;
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setCardProgress(Math.min(elapsed / duration * 100, 100));
    }, 50);
    const advanceTimer = setTimeout(() => {
      setActiveCard(prev => (prev + 1) % 4);
    }, duration);
    return () => {
      clearInterval(progressInterval);
      clearTimeout(advanceTimer);
    };
  }, [activeCard]);
  const cards = [{
    step: '1 of 4',
    question: 'What is your RCM software called and what are its top differentiators?',
    options: ['I will describe it', 'Generic RCM pitch']
  }, {
    step: '2 of 4',
    question: 'Who is the ideal decision maker you want to reach?',
    options: ['Office Manager', 'Practice Owner', 'Both']
  }, {
    step: '3 of 4',
    question: 'What email tone do you want?',
    options: ['Consultative', 'Direct and punchy', 'Warm and personal']
  }, {
    step: '4 of 4',
    question: 'How many leads do you want me to pull?',
    options: ['10-15 leads', '25-30 leads', '50+ leads']
  }];
  return <div className="w-full max-w-md mx-auto relative h-[380px] flex items-center justify-center" data-id="element-5597">
      {/* Stacked card shadows */}
      <div className="absolute inset-x-6 top-12 bottom-4 bg-slate-100 rounded-2xl -z-20 translate-y-6 scale-[0.90]" data-id="element-5598" />
      <div className="absolute inset-x-3 top-6 bottom-2 bg-slate-50 rounded-2xl border border-slate-200/50 -z-10 translate-y-3 scale-[0.95]" data-id="element-5599" />

      <div className="bg-white rounded-2xl border border-slate-200 w-full p-6 md:p-8 shadow-xl relative z-10" data-id="element-5600">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6" data-id="element-5601">
          <p className="text-[15px] text-slate-800 font-semibold leading-relaxed pr-4" data-id="element-5602">
            {cards[activeCard].question}
          </p>
          <div className="flex items-center gap-1.5 shrink-0 pt-1" data-id="element-5603">
            <button onClick={() => setActiveCard(Math.max(0, activeCard - 1))} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-30 disabled:hover:bg-transparent" disabled={activeCard === 0} data-id="element-5604">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-5605" />
            </button>
            <span className="tabular-nums font-medium text-slate-500 text-xs px-1.5" data-id="element-5606">
              {cards[activeCard].step}
            </span>
            <button onClick={() => setActiveCard(Math.min(3, activeCard + 1))} className="w-8 h-8 rounded-lg border border-blue-200 bg-blue-50 flex items-center justify-center text-blue-500 hover:bg-blue-100 hover:border-blue-300 transition-all disabled:opacity-30 disabled:bg-transparent disabled:border-slate-200 disabled:text-slate-400" disabled={activeCard === 3} data-id="element-5607">
              <ChevronRightIcon className="w-4 h-4" data-id="element-5608" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden mb-5" data-id="element-5609">
          <div className="h-full bg-blue-500 rounded-full transition-none" style={{
          width: `${cardProgress}%`
        }} data-id="element-5610" />
        </div>

        {/* Options */}
        <div className="space-y-2" data-id="element-5611">
          {cards[activeCard].options.map((opt, i) => <div key={i} className={`flex items-center gap-3.5 p-3.5 rounded-xl border transition-all cursor-pointer group ${i === 0 ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300'}`} data-id="element-5612">
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-5613">
                {i + 1}
              </div>
              <span className={`text-sm font-medium ${i === 0 ? 'text-blue-900' : 'text-slate-600'}`} data-id="element-5614">
                {opt}
              </span>
              {i === 0 && <ArrowRightIcon className="w-4 h-4 ml-auto text-blue-400" data-id="element-5615" />}
            </div>)}

          {/* Something else + Skip */}
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-slate-100" data-id="element-5616">
            <div className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 cursor-pointer transition-colors" data-id="element-5617">
              <PenLineIcon className="w-3.5 h-3.5" data-id="element-5618" />
              Something else
            </div>
            <button className="px-4 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-5619">
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function Step2Illustration() {
  return <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg mx-auto overflow-hidden" data-id="element-5620">
      <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-5621">
        <div className="flex items-center gap-2" data-id="element-5622">
          <TargetIcon className="w-4 h-4 text-blue-600" data-id="element-5623" />
          <span className="text-sm font-semibold text-slate-800" data-id="element-5624">
            Scoring Results
          </span>
        </div>
        <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded-md border border-slate-200" data-id="element-5625">
          142 leads found
        </span>
      </div>
      <div className="p-5 space-y-3" data-id="element-5626">
        {[{
        name: 'Capital City Roasters',
        locs: 3,
        rating: 3.6,
        score: 98
      }, {
        name: 'Austin Bean Co.',
        locs: 2,
        rating: 3.8,
        score: 92
      }, {
        name: 'Lone Star Coffee',
        locs: 4,
        rating: 3.9,
        score: 85
      }].map((lead, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.2
      }} className="p-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/30 transition-colors flex items-center justify-between" data-id="element-5627">
            <div data-id="element-5628">
              <div className="text-sm font-bold text-slate-900" data-id="element-5629">
                {lead.name}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-5630">
                {lead.locs} locations · {lead.rating}★ rating
              </div>
            </div>
            <div className="text-right" data-id="element-5631">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-5632">
                Fit Score
              </div>
              <div className="text-lg font-bold text-blue-600" data-id="element-5633">
                {lead.score}
              </div>
            </div>
          </motion.div>)}
      </div>
    </div>;
}
function Step3Illustration() {
  return <div className="w-full max-w-2xl mx-auto" data-id="element-5634">
      <div className="w-full rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-xl flex flex-col h-[420px] font-sans text-sm" data-id="element-5635">
        <div className="flex flex-1 overflow-hidden" data-id="element-5636">
          {/* Left: Progress sidebar */}
          <div className="hidden md:flex w-44 border-r border-slate-100 flex-col bg-slate-50/80 p-3" data-id="element-5637">
            <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-3 px-1" data-id="element-5638">
              Workflow
            </div>
            <div className="space-y-2.5 relative before:absolute before:inset-y-0 before:left-[7px] before:w-px before:bg-slate-200" data-id="element-5639">
              {[{
              label: 'Search businesses',
              done: true
            }, {
              label: 'Enrich contacts',
              done: true
            }, {
              label: 'Score leads',
              done: true
            }, {
              label: 'Draft outreach',
              active: true
            }, {
              label: 'Export results',
              done: false
            }].map((s, i) => <div key={i} className="flex items-center gap-2 relative z-10" data-id="element-5640">
                  <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${s.done ? 'bg-emerald-500 text-white' : s.active ? 'bg-blue-500 text-white ring-[3px] ring-blue-500/20' : 'bg-white border border-slate-200'}`} data-id="element-5641">
                    {s.done ? <CheckCircle2Icon className="w-2.5 h-2.5" data-id="element-5642" /> : s.active ? <Loader2Icon className="w-2 h-2 animate-spin" data-id="element-5643" /> : <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-5644" />}
                  </div>
                  <span className={`text-[10px] leading-tight ${s.done ? 'text-slate-400 line-through' : s.active ? 'text-slate-800 font-semibold' : 'text-slate-400'}`} data-id="element-5645">
                    {s.label}
                  </span>
                </div>)}
            </div>

            {/* Working folder */}
            <div className="mt-auto pt-3 border-t border-slate-200" data-id="element-5646">
              <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1" data-id="element-5647">
                Output files
              </div>
              <div className="space-y-1.5" data-id="element-5648">
                <div className="bg-white border border-slate-200 rounded-lg p-1.5 flex items-center gap-1.5" data-id="element-5649">
                  <FileSpreadsheetIcon className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-5650" />
                  <span className="text-[9px] text-slate-600 truncate flex-1" data-id="element-5651">
                    TX_Dental_Sales_List_RCM.xlsx
                  </span>
                  <span className="text-[8px] text-emerald-500 font-medium shrink-0" data-id="element-5652">
                    312 rows
                  </span>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg p-1.5 flex items-center gap-1.5" data-id="element-5653">
                  <FileIcon className="w-3 h-3 text-blue-500 shrink-0" data-id="element-5654" />
                  <span className="text-[9px] text-slate-600 truncate flex-1" data-id="element-5655">
                    Scores.csv
                  </span>
                  <Loader2Icon className="w-2.5 h-2.5 text-blue-500 animate-spin shrink-0" data-id="element-5656" />
                </div>
                <div className="bg-white border border-slate-100 rounded-lg p-1.5 flex items-center gap-1.5 opacity-50" data-id="element-5657">
                  <SendIcon className="w-3 h-3 text-slate-400 shrink-0" data-id="element-5658" />
                  <span className="text-[9px] text-slate-400 truncate flex-1" data-id="element-5659">
                    Emails.md
                  </span>
                  <span className="text-[8px] text-slate-400 shrink-0" data-id="element-5660">
                    pending
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main execution area */}
          <div className="flex-1 flex flex-col bg-white overflow-hidden" data-id="element-5661">
            {/* Top bar */}
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-white" data-id="element-5662">
              <div className="flex items-center gap-2" data-id="element-5663">
                <div className="w-5 h-5 rounded-md bg-blue-600 flex items-center justify-center" data-id="element-5664">
                  <SparklesIcon className="w-3 h-3 text-white" data-id="element-5665" />
                </div>
                <span className="text-[12px] font-semibold text-slate-800" data-id="element-5666">
                  Dental clinics in Columbus, OH
                </span>
              </div>
              <span className="text-[10px] text-slate-400 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100" data-id="element-5667">
                Step 4 of 5
              </span>
            </div>

            {/* Content area */}
            <div className="flex-1 overflow-y-auto p-3.5 space-y-2.5" data-id="element-5668">
              {/* Completed steps - collapsed */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/60 border border-blue-100 rounded-lg" data-id="element-5669">
                <CheckCircle2Icon className="w-3 h-3 text-blue-500 shrink-0" data-id="element-5670" />
                <span className="text-[10px] text-blue-700 font-medium" data-id="element-5671">
                  Found 312 clinics
                </span>
                <span className="text-[10px] text-blue-400 mx-1" data-id="element-5672">·</span>
                <span className="text-[10px] text-blue-700 font-medium" data-id="element-5673">
                  Enriched contacts
                </span>
                <ChevronDownIcon className="w-3 h-3 text-blue-400 ml-auto" data-id="element-5674" />
              </div>

              {/* Scoring breakdown */}
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 space-y-2" data-id="element-5675">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-5676">
                  <CheckCircle2Icon className="w-3 h-3 text-blue-500" data-id="element-5677" />
                  <span className="text-[11px] font-semibold text-slate-700" data-id="element-5678">
                    Lead scoring complete
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px]" data-id="element-5679">
                  <span className="text-slate-500" data-id="element-5680">Tier A (high fit)</span>
                  <span className="font-semibold text-slate-700" data-id="element-5681">48 leads</span>
                </div>
                <div className="flex items-center justify-between text-[11px]" data-id="element-5682">
                  <span className="text-slate-500" data-id="element-5683">Tier B (medium fit)</span>
                  <span className="font-semibold text-slate-700" data-id="element-5684">
                    127 leads
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px]" data-id="element-5685">
                  <span className="text-slate-500" data-id="element-5686">Tier C (low fit)</span>
                  <span className="font-medium text-slate-400" data-id="element-5687">137 leads</span>
                </div>
              </div>

              {/* Credit approval - compact, blue palette */}
              <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-3" data-id="element-5688">
                <div className="flex items-center gap-2 mb-2" data-id="element-5689">
                  <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-5690">
                    <DatabaseIcon className="w-2.5 h-2.5 text-blue-600" data-id="element-5691" />
                  </div>
                  <span className="text-[11px] font-semibold text-blue-900" data-id="element-5692">
                    Draft outreach for 48 Tier A leads
                  </span>
                  <span className="ml-auto text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full" data-id="element-5693">
                    96 credits
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-5694">
                  <button className="px-3.5 py-1.5 rounded-lg text-[10px] font-semibold text-white bg-blue-600 shadow-sm" data-id="element-5695">
                    Approve
                  </button>
                  <button className="px-3.5 py-1.5 rounded-lg text-[10px] font-medium text-blue-700 border border-blue-200 bg-white" data-id="element-5696">
                    Skip
                  </button>
                  <button className="ml-auto text-[10px] text-blue-500 flex items-center gap-1" data-id="element-5697">
                    <SettingsIcon className="w-3 h-3" data-id="element-5698" />
                    Adjust
                  </button>
                </div>
              </div>

              {/* Active status */}
              <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1" data-id="element-5699">
                <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-5700">
                  <SparklesIcon className="w-3.5 h-3.5 text-blue-500" data-id="element-5701" />
                </motion.div>
                Waiting for approval to draft emails...
              </div>
            </div>

            {/* Bottom status bar */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-5702">
              <div className="flex items-center gap-2 text-[10px] text-slate-400" data-id="element-5703">
                <ClockIcon className="w-3 h-3" data-id="element-5704" />
                Awaiting approval
              </div>
              <div className="text-[10px] text-slate-400" data-id="element-5705">
                Balance:{' '}
                <span className="font-semibold text-slate-600" data-id="element-5706">
                  4,200 credits
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
const illustrations = [Step1Illustration, Step2Illustration, Step3Illustration];
export function OpenclawWorkflow() {
  const [activeStep, setActiveStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setProgress(Math.min(elapsed / STEP_DURATION * 100, 100));
    }, 50);
    timerRef.current = setTimeout(() => {
      setActiveStep(prev => prev === steps.length ? 1 : prev + 1);
    }, STEP_DURATION);
  }, []);
  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [activeStep, startTimer]);
  const handleStepClick = (stepId: number) => {
    setActiveStep(stepId);
  };
  const ActiveIllustration = illustrations[activeStep - 1];
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5707">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-5708">
        <div className="max-w-3xl mb-16" data-id="element-5709">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-5710">
            How it works
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed" data-id="element-5711">
            The agent acts as your dedicated sales researcher. It understands
            your goals, queries our massive local business database, and
            prepares everything you need for outreach.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-5712">
          {/* Left: Steps Nav */}
          <div className="lg:col-span-4 space-y-3 relative" data-id="element-5713">
            <div className="absolute left-[27px] top-10 bottom-10 w-0.5 bg-slate-100 hidden lg:block" data-id="element-5714" />
            {steps.map(step => {
            const isActive = activeStep === step.id;
            return <button key={step.id} onClick={() => handleStepClick(step.id)} className={`w-full text-left p-5 rounded-2xl transition-all duration-300 relative z-10 ${isActive ? 'bg-white shadow-lg shadow-slate-200/50 ring-1 ring-slate-200/50' : 'hover:bg-slate-50'}`} data-id="element-5715">
                  <div className="flex gap-4" data-id="element-5716">
                    <div className="relative mt-1 shrink-0" data-id="element-5717">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-5718">
                        {step.id}
                      </div>
                    </div>
                    <div data-id="element-5719">
                      <h3 className={`text-lg font-bold mb-1 ${isActive ? 'text-slate-900' : 'text-slate-500'}`} data-id="element-5720">
                        {step.title}
                      </h3>
                      <AnimatePresence initial={false} data-id="element-5721">
                        {isActive && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.3
                    }} className="overflow-hidden" data-id="element-5722">
                            <p className="text-sm text-slate-500 leading-relaxed pt-2 pb-4" data-id="element-5723">
                              {step.description}
                            </p>
                            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-5724">
                              <div className="h-full bg-blue-600 rounded-full transition-none" style={{
                          width: `${progress}%`
                        }} data-id="element-5725" />
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Visual Area */}
          <div className="lg:col-span-8" data-id="element-5726">
            <div className="relative rounded-3xl bg-slate-50 border border-slate-100 p-6 md:p-10 overflow-hidden min-h-[500px] flex items-center justify-center" data-id="element-5727">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-blue-400/[0.08] blur-[100px] rounded-full pointer-events-none" data-id="element-5728" />
              <div className="relative z-10 w-full flex justify-center" data-id="element-5729">
                <AnimatePresence mode="wait" data-id="element-5730">
                  <motion.div key={activeStep} initial={{
                  opacity: 0,
                  y: 20,
                  scale: 0.97
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.97
                }} transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }} className="w-full" data-id="element-5731">
                    <ActiveIllustration data-id="element-5732" />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}